import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import {
  Microphone, MicrophoneSlash, X as CloseIcon, Phone, ArrowLeft, Sparkle,
} from "@phosphor-icons/react";

/**
 * DriveMode — modalità in macchina, voice-only.
 *
 * Flow conversazionale:
 *   1. Idle: bottone mic gigante. Tap → recording.
 *   2. Recording: pulsa rosso, waveform finta, auto-stop a 8s o tap stop.
 *   3. Processing: spinner + "Sento…".
 *   4. Reply: il browser legge la risposta via speechSynthesis (italiano).
 *   5. Auto-loop: dopo 1.5s di silenzio post-TTS, ricomincia listening.
 *
 * Niente schermo da guardare. L'utente in macchina può anche lasciare il
 * telefono nel cruscotto e parlare a voce alta — il bottone è enorme,
 * touchable senza guardare.
 */
export default function DriveMode() {
  const nav = useNavigate();
  const [stage, setStage] = useState("idle"); // idle | recording | processing | replying
  const [transcript, setTranscript] = useState("");
  const [reply, setReply] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [history, setHistory] = useState([]); // [{transcript, reply}]
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const stopTimerRef = useRef(null);
  const restartTimerRef = useRef(null);
  const wantingRef = useRef(true); // true if loop should keep running

  const speak = (text, onEnd) => {
    if (!text) { onEnd?.(); return; }
    try {
      const synth = window.speechSynthesis;
      if (!synth) { onEnd?.(); return; }
      synth.cancel();
      const utter = new SpeechSynthesisUtterance(text);
      utter.lang = "it-IT";
      // Pick best Italian voice if available
      const voices = synth.getVoices();
      const italian = voices.find((v) => /it[-_]/i.test(v.lang));
      if (italian) utter.voice = italian;
      utter.rate = 1.02;
      utter.pitch = 1.0;
      utter.onend = () => onEnd?.();
      utter.onerror = () => onEnd?.();
      synth.speak(utter);
    } catch {
      onEnd?.();
    }
  };

  const cleanupRecording = () => {
    if (stopTimerRef.current) { clearTimeout(stopTimerRef.current); stopTimerRef.current = null; }
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      try { mediaRef.current.stop(); } catch { /* noop */ }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    mediaRef.current = null;
    chunksRef.current = [];
  };

  const sendAudio = async (blob) => {
    if (blob.size < 800) {
      setStage("idle");
      return;
    }
    setStage("processing");
    setErrorMsg("");
    try {
      const fd = new FormData();
      const filename = blob.type.includes("mp4") ? "drive.m4a" : "drive.webm";
      fd.append("file", blob, filename);
      const { data } = await api.post(`/drive/command`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 45_000,
      });
      setTranscript(data.transcript || "");
      setReply(data.reply || "");
      setHistory((h) => [{ transcript: data.transcript, reply: data.reply, action: data.action }, ...h].slice(0, 5));
      setStage("replying");
      // Special action: tel: link
      if (data.action === "call" && data.payload?.phone) {
        speak(data.reply, () => {
          window.location.href = `tel:${data.payload.phone}`;
          setStage("idle");
        });
        return;
      }
      speak(data.reply, () => {
        setStage("idle");
        if (wantingRef.current) {
          // Auto-restart listening dopo breve pausa
          restartTimerRef.current = setTimeout(() => {
            if (wantingRef.current) startRecording();
          }, 1300);
        }
      });
    } catch (err) {
      setErrorMsg(apiError(err) || "Non ti ho sentito. Riprova quando vuoi.");
      setStage("idle");
    }
  };

  const startRecording = async () => {
    if (stage === "recording" || stage === "processing") return;
    setErrorMsg("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "audio/webm");
      const mr = new MediaRecorder(stream, { mimeType: mime });
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        cleanupRecording();
        sendAudio(blob);
      };
      mr.start();
      setStage("recording");
      // Auto-stop a 8s
      stopTimerRef.current = setTimeout(() => {
        if (mediaRef.current && mediaRef.current.state === "recording") {
          mediaRef.current.stop();
        }
      }, 8000);
    } catch (err) {
      setErrorMsg("Permesso microfono negato. Controlla le impostazioni del telefono.");
      setStage("idle");
    }
  };

  const stopRecording = () => {
    if (mediaRef.current && mediaRef.current.state === "recording") {
      mediaRef.current.stop();
    }
  };

  const exit = () => {
    wantingRef.current = false;
    cleanupRecording();
    if (restartTimerRef.current) clearTimeout(restartTimerRef.current);
    try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
    nav("/dashboard");
  };

  useEffect(() => {
    // Keep screen awake while in drive mode (best effort)
    let wakeLock = null;
    if ("wakeLock" in navigator) {
      navigator.wakeLock.request("screen").then((w) => { wakeLock = w; }).catch(() => {});
    }
    // Pre-warm voices list
    try { window.speechSynthesis?.getVoices(); } catch { /* noop */ }
    return () => {
      wantingRef.current = false;
      cleanupRecording();
      if (restartTimerRef.current) clearTimeout(restartTimerRef.current);
      try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
      try { wakeLock?.release(); } catch { /* noop */ }
    };
  }, []);

  const big = stage === "recording";
  const proc = stage === "processing";
  const reply_active = stage === "replying";

  return (
    <div className="fixed inset-0 bg-[#0A0A09] text-[#F1F4ED] flex flex-col" data-testid="drive-mode">
      {/* Top bar */}
      <div className="px-5 pt-5 pb-2 flex items-center justify-between">
        <button
          onClick={exit}
          className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center"
          aria-label="Esci"
          data-testid="drive-exit"
        >
          <ArrowLeft size={22} weight="bold" />
        </button>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#7C8B6E] animate-pulse" />
          <span className="text-[11px] uppercase tracking-[0.22em] text-[#A8B89A] font-semibold">In macchina</span>
        </div>
        <div className="w-11 h-11" />
      </div>

      {/* Center: big mic button */}
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        <button
          onClick={big ? stopRecording : (proc || reply_active ? undefined : startRecording)}
          disabled={proc}
          className={`relative w-44 h-44 sm:w-56 sm:h-56 rounded-full flex items-center justify-center transition-all ${
            big
              ? "bg-[#E63946] shadow-[0_0_120px_rgba(230,57,70,0.5)]"
              : reply_active
                ? "bg-[#7C8B6E] shadow-[0_0_80px_rgba(124,139,110,0.4)]"
                : proc
                  ? "bg-[#3F3D38]"
                  : "bg-[#7C8B6E] hover:bg-[#90A082] shadow-[0_0_60px_rgba(124,139,110,0.3)]"
          }`}
          data-testid="drive-mic-btn"
        >
          {big && <span className="absolute inset-0 rounded-full bg-[#E63946] animate-ping opacity-30" />}
          {reply_active && <span className="absolute inset-0 rounded-full bg-[#7C8B6E] animate-ping opacity-25" />}
          {proc ? (
            <div className="w-12 h-12 rounded-full border-4 border-[#A8B89A] border-t-transparent animate-spin" />
          ) : big ? (
            <MicrophoneSlash size={68} weight="fill" className="text-white" />
          ) : (
            <Microphone size={68} weight="fill" className="text-white" />
          )}
        </button>

        <div className="mt-10 text-center min-h-[64px]">
          {stage === "idle" && !transcript && (
            <p className="text-[18px] sm:text-xl text-[#A8B89A] font-medium">
              Tocca il microfono e parla
            </p>
          )}
          {stage === "idle" && transcript && (
            <>
              <p className="text-[12px] uppercase tracking-[0.2em] text-[#7C8B6E] font-semibold mb-2">Pronto risponde</p>
              <p className="text-[18px] text-[#F1F4ED] leading-snug max-w-md mx-auto" data-testid="drive-last-reply">
                {reply}
              </p>
            </>
          )}
          {big && (
            <p className="text-[20px] text-[#E63946] font-semibold animate-pulse" data-testid="drive-status-listening">
              Ti ascolto…
            </p>
          )}
          {proc && (
            <p className="text-[18px] text-[#A8B89A]" data-testid="drive-status-processing">
              Sto pensando…
            </p>
          )}
          {reply_active && (
            <>
              <p className="text-[12px] uppercase tracking-[0.2em] text-[#7C8B6E] font-semibold mb-2">Pronto sta parlando</p>
              <p className="text-[18px] text-[#F1F4ED] leading-snug max-w-md mx-auto" data-testid="drive-status-reply">
                {reply}
              </p>
            </>
          )}
        </div>

        {errorMsg && (
          <p className="mt-4 text-[14px] text-[#E63946]" data-testid="drive-error">{errorMsg}</p>
        )}
      </div>

      {/* Bottom: examples + history */}
      <div className="px-5 pb-7 max-w-md mx-auto w-full">
        {history.length === 0 ? (
          <div className="text-center">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#6B6A65] mb-3 flex items-center justify-center gap-1.5">
              <Sparkle size={11} weight="fill" className="text-amber-500" />
              Prova a dire
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {[
                "Cosa ho oggi",
                "Ricordami di chiamare Bianchi venerdì",
                "Memo: il signor Ferrari cerca trilocale",
              ].map((s) => (
                <span key={s} className="px-3 py-1.5 rounded-full bg-white/6 border border-white/10 text-[12px] text-[#A8B89A]">
                  "{s}"
                </span>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-2 max-h-32 overflow-y-auto" data-testid="drive-history">
            {history.slice(0, 3).map((h, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-2.5">
                <div className="text-[11px] uppercase tracking-[0.16em] text-[#7C8B6E] font-semibold mb-0.5">Tu</div>
                <div className="text-[13px] text-[#F1F4ED] mb-1.5 italic">"{h.transcript}"</div>
                <div className="text-[11px] uppercase tracking-[0.16em] text-amber-500/80 font-semibold mb-0.5">Pronto</div>
                <div className="text-[13px] text-[#D4DCC9]">{h.reply}</div>
              </div>
            ))}
          </div>
        )}
        <p className="mt-4 text-center text-[10.5px] text-[#6B6A65] leading-relaxed">
          Tieni il telefono nel cruscotto. Niente schermo da guardare.<br />
          Per uscire: tocca la freccia in alto a sinistra.
        </p>
      </div>
    </div>
  );
}
